import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  confirmDemoBooking,
  fetchAvailableSlots,
  fetchDemoEnquiry,
} from "../../Moduels/demo/bookDemoApi";
import DemoChrome from "./DemoChrome";
import "./auth.css";
import "./bookDemo.css";

const HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, index) => String(index * 5).padStart(2, "0"));

function todayIST() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function formatDisplayDate(isoDate) {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function ScheduledCard({ booking }) {
  const meetReady = booking?.meeting_status === "MEETING_READY"
    && booking?.meeting_url
    && String(booking.meeting_url).startsWith("https://");
  const meetingUrl = meetReady ? booking.meeting_url : "";

  return (
    <div className="bd-card bd-card--confirm">
      <div className="bd-success-icon" aria-hidden="true">
        <svg width="36" height="36" viewBox="0 0 52 52" fill="none">
          <circle cx="26" cy="26" r="25" stroke="currentColor" strokeWidth="2" />
          <path d="M16 26l8 8 12-16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h1 className="bd-title" style={{ fontSize: "1.65rem" }}>Demo Scheduled!</h1>
      <p className="bd-subtitle" style={{ marginBottom: "1.25rem" }}>
        Your BrixiGo demo has been successfully scheduled.
      </p>
      <dl className="bd-details">
        <div>
          <dt>Date</dt>
          <dd>{formatDisplayDate(booking.preferred_date)}</dd>
        </div>
        <div>
          <dt>Time</dt>
          <dd>{booking.schedule_label || `${booking.preferred_time} IST`}</dd>
        </div>
        <div>
          <dt>Duration</dt>
          <dd>{booking.duration_minutes} minutes</dd>
        </div>
        <div>
          <dt>Meeting</dt>
          <dd>{meetReady ? "Google Meet" : "Meet link unavailable"}</dd>
        </div>
        {booking.reference_id ? (
          <div>
            <dt>Reference</dt>
            <dd>{booking.reference_id}</dd>
          </div>
        ) : null}
      </dl>
      {meetReady ? (
        <a className="bd-submit" href={meetingUrl} target="_blank" rel="noreferrer">
          Join Google Meet
        </a>
      ) : (
        <p className="bd-hint">
          Your demo time is reserved. A Google Meet link will be sent when it is available.
        </p>
      )}
      <p className="bd-hint" style={{ marginTop: "0.85rem" }}>
        {booking.confirmation_sent
          ? "A confirmation has been sent to your email."
          : "Your demo is saved. The confirmation email could not be sent just now."}
      </p>
    </div>
  );
}

export default function ScheduleDemoPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const minDate = useMemo(() => todayIST(), []);

  const [enquiry, setEnquiry] = useState(null);
  const [pageError, setPageError] = useState("");
  const [loadingEnquiry, setLoadingEnquiry] = useState(true);

  const [selectedDate, setSelectedDate] = useState(minDate);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [demoDuration, setDemoDuration] = useState(60);

  const [selectedSlot, setSelectedSlot] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [customHour, setCustomHour] = useState("11");
  const [customMinute, setCustomMinute] = useState("30");
  const [customPeriod, setCustomPeriod] = useState("AM");
  const [customTime, setCustomTime] = useState("");
  const [timeError, setTimeError] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadingEnquiry(true);
    setPageError("");
    fetchDemoEnquiry(token)
      .then((data) => {
        if (!active) return;
        setEnquiry(data);
      })
      .catch((err) => {
        if (!active) return;
        setPageError(err?.message || "This scheduling link is not valid.");
      })
      .finally(() => {
        if (active) setLoadingEnquiry(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const loadSlots = useCallback(async (date) => {
    if (!date) return;
    setSlotsLoading(true);
    setSlotsError("");
    try {
      const data = await fetchAvailableSlots(date);
      setAvailableSlots(data?.available_slots || []);
      setBookedSlots(data?.booked_slots || []);
      setAllSlots(data?.all_slots || []);
      if (data?.duration_minutes) setDemoDuration(data.duration_minutes);
    } catch (err) {
      setAvailableSlots([]);
      setBookedSlots([]);
      setAllSlots([]);
      setSlotsError(err?.message || "We could not load available times. Please try again.");
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enquiry || enquiry.is_scheduled) return;
    loadSlots(selectedDate);
  }, [enquiry, selectedDate, loadSlots]);

  const chosenTime = customTime || selectedSlot;

  const applyCustomTime = () => {
    const label = `${customHour}:${customMinute} ${customPeriod}`;
    setCustomTime(label);
    setSelectedSlot("");
    setTimeError("");
  };

  const handleConfirm = async () => {
    if (confirming) return;
    if (!selectedDate) {
      setTimeError("Please choose a date.");
      return;
    }
    if (selectedDate < minDate) {
      setTimeError("Please choose a current or future date.");
      return;
    }
    if (!chosenTime) {
      setTimeError("Please select a time.");
      return;
    }
    setConfirming(true);
    setTimeError("");
    try {
      const result = await confirmDemoBooking({
        access_token: token,
        preferred_date: selectedDate,
        preferred_time: chosenTime,
        is_custom_time: Boolean(customTime),
      });
      setEnquiry(result);
    } catch (err) {
      const message = err?.message || "We could not schedule that time. Please try again.";
      setTimeError(message);
      if (message.toLowerCase().includes("available") || message.toLowerCase().includes("another")) {
        setSelectedSlot("");
        setCustomTime("");
        await loadSlots(selectedDate);
      }
    } finally {
      setConfirming(false);
    }
  };

  const greeting = enquiry?.full_name?.trim()
    ? `Hi ${enquiry.full_name.trim()}, choose a convenient date and time for your personalized BrixiGo demo.`
    : "Choose a convenient date and time for your personalized BrixiGo demo.";

  return (
    <DemoChrome backLabel="Back" onBack={() => navigate(-1)}>
      {loadingEnquiry ? (
        <div className="bd-card bd-card--confirm">
          <p className="bd-slots-loading">Loading your demo request…</p>
        </div>
      ) : pageError ? (
        <div className="bd-card bd-card--confirm">
          <h1 className="bd-title" style={{ fontSize: "1.4rem" }}>Unable to open scheduling</h1>
          <p className="bd-form-error" role="alert">{pageError}</p>
          <button type="button" className="bd-submit" onClick={() => navigate("/book-demo")}>
            Book a Demo
          </button>
        </div>
      ) : enquiry?.is_scheduled ? (
        <ScheduledCard booking={enquiry} />
      ) : (
        <div className="bd-card">
          <div className="bd-hero" style={{ marginBottom: "1.25rem" }}>
            <span className="bd-eyebrow">Optional scheduling</span>
            <h1 className="bd-title" style={{ fontSize: "1.7rem" }}>Schedule Your BrixiGo Demo</h1>
            <p className="bd-subtitle">{greeting}</p>
            {enquiry?.work_email ? (
              <p className="bd-hint" style={{ marginTop: "0.75rem" }}>Booking for {enquiry.work_email}</p>
            ) : null}
          </div>

          <div className="bd-field">
            <label className="bd-label" htmlFor="bd-schedule-date">Select Date</label>
            <div className="field-wrapper">
              <input
                id="bd-schedule-date"
                type="date"
                min={minDate}
                className="field-input"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedSlot("");
                  setCustomTime("");
                  setTimeError("");
                }}
              />
            </div>
          </div>

          <div className="bd-field" style={{ marginTop: "1.25rem" }}>
            <p className="bd-label">Available Times</p>
            <p className="bd-hint" style={{ textAlign: "left" }}>Times are shown in IST.</p>
            {slotsLoading ? (
              <p className="bd-slots-loading">Checking availability…</p>
            ) : slotsError ? (
              <div>
                <p className="bd-form-error" role="alert">{slotsError}</p>
                <button type="button" className="bd-text-btn" onClick={() => loadSlots(selectedDate)}>
                  Try again
                </button>
              </div>
            ) : (
              <div className="bd-slots" role="group" aria-label="Available times">
                {allSlots.filter((slot) => availableSlots.includes(slot) || bookedSlots.includes(slot)).map((slot) => {
                  const isBooked = bookedSlots.includes(slot);
                  const isSelected = !customTime && selectedSlot === slot;
                  return (
                    <button
                      key={slot}
                      type="button"
                      className={`bd-slot ${isSelected ? "bd-slot--selected" : ""} ${isBooked ? "bd-slot--booked" : ""}`}
                      disabled={isBooked}
                      aria-pressed={isSelected}
                      onClick={() => {
                        if (isBooked) return;
                        setSelectedSlot(slot);
                        setCustomTime("");
                        setCustomOpen(false);
                        setTimeError("");
                      }}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
            )}
            {!slotsLoading && !slotsError && availableSlots.length === 0 && (
              <p className="bd-hint" style={{ textAlign: "left", marginTop: "0.75rem" }}>
                No open times remain on this date. Choose another date or a custom time.
              </p>
            )}
          </div>

          <div className="bd-or" aria-hidden="true"><span>OR</span></div>

          <button
            type="button"
            className={`bd-slot bd-slot--custom-option ${customOpen || customTime ? "bd-slot--selected" : ""}`}
            onClick={() => {
              setCustomOpen((open) => !open);
              setTimeError("");
            }}
          >
            Choose Custom Time
          </button>

          {customOpen && (
            <div className="bd-custom-time">
              <p className="bd-label">Choose Your Preferred Time</p>
              <p className="bd-hint" style={{ textAlign: "left" }}>Date: {formatDisplayDate(selectedDate)}</p>
              <div className="bd-time-row">
                <label className="bd-sr" htmlFor="bd-hour">Hour</label>
                <select id="bd-hour" className="field-input" value={customHour} onChange={(e) => setCustomHour(e.target.value)}>
                  {HOURS.map((hour) => <option key={hour} value={hour}>{hour}</option>)}
                </select>
                <span className="bd-time-colon">:</span>
                <label className="bd-sr" htmlFor="bd-minute">Minute</label>
                <select id="bd-minute" className="field-input" value={customMinute} onChange={(e) => setCustomMinute(e.target.value)}>
                  {MINUTES.map((minute) => <option key={minute} value={minute}>{minute}</option>)}
                </select>
                <label className="bd-sr" htmlFor="bd-period">AM or PM</label>
                <select id="bd-period" className="field-input" value={customPeriod} onChange={(e) => setCustomPeriod(e.target.value)}>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
              <p className="bd-hint" style={{ textAlign: "left" }}>Duration: {demoDuration} minutes</p>
              <button type="button" className="bd-text-btn" onClick={applyCustomTime}>
                Continue
              </button>
              {customTime && <p className="bd-selected-time">Selected custom time: {customTime} IST</p>}
            </div>
          )}

          {chosenTime && (
            <p className="bd-selected-time">
              {formatDisplayDate(selectedDate)} at {chosenTime} IST · {demoDuration} minutes
            </p>
          )}
          {timeError && <p className="bd-form-error" role="alert">{timeError}</p>}

          <div className="bd-field" style={{ marginTop: "1.25rem" }}>
            <p className="bd-label">Duration</p>
            <p className="bd-selected-time">{demoDuration} minutes</p>
          </div>

          <div className="bd-actions">
            <button
              type="button"
              className={`bd-submit ${confirming ? "loading" : ""}`}
              disabled={confirming || !chosenTime}
              onClick={handleConfirm}
            >
              {confirming ? <><span className="spinner" /> Confirming…</> : "Confirm Demo"}
            </button>
            <p className="bd-hint">You can leave this page. Your enquiry stays saved.</p>
          </div>
        </div>
      )}
    </DemoChrome>
  );
}
